import { Suspense } from 'react';
import _ from 'lodash';
import {
    Icon,
    SuspenseBoundary,
    BillbackDetailsHeader,
    BillbackInfo,
    DisputeButton,
    FileList,
    LoaderOverlay
} from 'components';
import { 
    useDeductionDetails,
    useBillbackDocuments,
    useRefreshDeductionDetails,
} from 'state';
import {
    PaymentDetail,
    BillbackDetailHeaderData,
    BillbackInfoData,
} from 'types';
import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons'
import { isPaidDeduction } from 'functions';
import { useBackupDocument, useInvoiceParams } from 'hooks';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import styles from './BillbackDetailsPage.module.scss';

type BillbackDetailsProps = {
    billbackDetails: any;
};

export default function BillbackDetailsPage() {
    const { accountId, vendorId, invoiceId } = useInvoiceParams();
    const billbackDetails = useDeductionDetails(accountId, vendorId, invoiceId);
    const refresh = useRefreshDeductionDetails(accountId, vendorId, invoiceId);
   
    return (
        <SuspenseBoundary
            errorMessage={`Error retrieving billback details ${invoiceId}`}
            onReset={refresh}
        >
            <BillbackDetailsComponent 
                billbackDetails={billbackDetails}
            />
        </SuspenseBoundary>
    );
}

const BillbackDetailsComponent = ({ billbackDetails }: BillbackDetailsProps) => {
    const { vendorId } = useInvoiceParams();

    const getHeaderData = (): BillbackDetailHeaderData => {
        return {
            type: billbackDetails.marBillTypeDescription ?? "Not Found",
            invoiceDate: billbackDetails.invoiceDate ? new Date(billbackDetails.invoiceDate).toLocaleDateString() : "",
            paymentIdRef: billbackDetails.paymentId,
            billbackNumber: billbackDetails.invoiceId,
            billbackAmount: billbackDetails.adjustmentAmount,
            vendorId: vendorId,
            vendorName: '',
        };
    }
    const billbackDetailHeader: BillbackDetailHeaderData = getHeaderData();

    const getBillbackInfo = (): BillbackInfoData => {
        return {
            billbackNumber: billbackDetails.invoiceId,
            billbackAmount: billbackDetails.adjustmentAmount,
            referenceNumber: billbackDetails.customerItemReference,
            voucherId: billbackDetails.voucherId ?? 'Not Found',
        }
    };
    const billbackInfo: BillbackInfoData = getBillbackInfo();

    return (
        <div className="details-page">
            <div className='details-page-title'>
                <Icon icon={faMoneyBillTransfer}/> 
                Billback Details
            </div>
            <div>
                <div className={styles.buttonContainer}>
                    <DisputeButton 
                        selectedDetails={[billbackDetails]} 
                        context="billbackDetails"
                        disabled={!isPaidDeduction(billbackDetails.statusDescription)}
                    />
                </div>
            </div>
            <div className="details-page-main">
                <div className={styles.header}>
                    <BillbackDetailsHeader billbackDetailHeader={billbackDetailHeader}/>
                </div>
                <div className={styles.mainContent}>
                    <div className={styles.billbackInfoContainer}>
                        <BillbackInfo billbackInfo={billbackInfo}/>
                    </div>
                    <div className={styles.backupDocumentationContainer}>
                        <div className={styles.backupDocumentationTitle}>Backup Documentation</div>
                        <div className={styles.backupDocumentationFiles}>
                            <Suspense fallback={<LoaderOverlay isLoading />}>
                                {<Documents detail={billbackDetails} />}
                            </Suspense>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

type DetailProps = {
    detail: PaymentDetail
};

const Documents = ({ detail }: DetailProps) => {
    const documents = useBillbackDocuments(detail);
    const getDocument = useBackupDocument();
    
    return _.isEmpty(documents)
        ? <span className={styles.none}>No documents found</span>
        : <>
            <FileList documents={documents} onClick={getDocument} />
            {getDocument.showError && <DocumentErrorDialog onClose={() => getDocument.setShowError(false)} documentIdentifier={getDocument.errorIdentifier} />}
          </>;
};
